import DashboardControls from "@/components/admin/dashboard-controls";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { ArrowRight, Bell, ChefHat, DollarSign, ShoppingBag, Check, X, Wallet, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatIDR } from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { playNewOrderChime, playWaiterCallAlert, unlockAudio } from "@/lib/sound";
import { useAdminSocket } from "@/lib/useAdminSocket";
import OrderBoard, { DashboardKpi, OrderDetails } from "@/components/admin/order-board";

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
}

export default function AdminDashboard() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback(outletId);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    total_orders: 0, total_revenue: 0, pending: 0, preparing: 0,
    paid: 0, unpaid: 0, paid_revenue: 0, unpaid_revenue: 0,
  });
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("resto_sound") !== "off");
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;
  const [waStatus, setWaStatus] = useState(null);
  const [waTesting, setWaTesting] = useState(false);
  const [updatingIds, setUpdatingIds] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const detailOrder = orders.find((order) => order.id === detailId);
  useEffect(() => { setDetailId(null); }, [outletId]);

  const loadWaStatus = useCallback(async () => {
    if (!outletId) return;
    try {
      const { data } = await api.get("/whatsapp/status", { params: { outlet_id: outletId } });
      setWaStatus(data);
    } catch {
      setWaStatus(null);
    }
  }, [outletId]);

  const testWhatsApp = async () => {
    if (!outletId) return;
    setWaTesting(true);
    try {
      await api.post("/whatsapp/test", { outlet_id: outletId });
      toast.success("WhatsApp test message sent. Check the staff phone.");
    } catch (err) {
      toast.error("Unable to send the WhatsApp test message. Please try again.");
    } finally {
      setWaTesting(false);
    }
  };

  const loadAll = useCallback(() => run(async () => {
    if (!outletId) return;
    const [o, s, w] = await Promise.all([
      api.get("/orders", { params: { outlet_id: outletId } }),
      api.get("/orders-stats", { params: { outlet_id: outletId } }),
      api.get("/waiter-calls", { params: { outlet_id: outletId, status: "pending" } }),
    ]);
    setOrders(o.data);
    setStats(s.data);
    setWaiterCalls(w.data || []);
  }), [outletId, run]);

  // Realtime WS
  const handleEvent = useCallback((msg) => {
    if (!msg?.event) return;
    if (msg.payload?.outlet_id && msg.payload.outlet_id !== outletId) return;
    if (msg.event === "order_created") {
      if (soundOnRef.current) {
        // Ensure audio context is ready
        unlockAudio();
        setTimeout(() => playNewOrderChime(), 50);
      }
      toast.success(`New order from Table ${msg.payload?.table_number || "?"}`, { duration: 5000 });
      loadAll();
    } else if (msg.event === "order_updated") {
      loadAll();
     } else if (msg.event === "waiter_call_created") {
       if (soundOnRef.current) {
         // Ensure audio context is ready
         unlockAudio();
         setTimeout(() => playWaiterCallAlert(), 50);
       }
       toast.info(`🔔 Waiter call from Table ${msg.payload?.table_number || "?"}`, { duration: 5000 });
       loadAll();
    } else if (msg.event === "waiter_call_updated") {
      loadAll();
    }
  }, [loadAll, outletId]);

  const { connected } = useAdminSocket(handleEvent);

  useEffect(() => {
    loadAll();
    loadWaStatus();
    // Fallback poll every 30s if WS is dropped
    const iv = setInterval(loadAll, 30000);
    return () => clearInterval(iv);
  }, [loadAll, loadWaStatus]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("resto_sound", next ? "on" : "off");
    if (next) {
      unlockAudio();
      playNewOrderChime();
      toast.success("Sound notifications enabled");
    } else {
      toast.info("Sound notifications disabled");
    }
  };

  const printKitchen = (orderId) => {
    window.open(`/kitchen-print/${orderId}`, "_blank", "noopener,noreferrer");
  };

  const updateStatus = async (orderId, status) => {
    setUpdatingIds((ids) => [...ids, orderId]);
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Status: ${status}`);
      loadAll();
    } catch {
      toast.error("Unable to update the order status");
    } finally {
      setUpdatingIds((ids) => ids.filter((id) => id !== orderId));
    }
  };

  const handleWaiterCall = async (callId, newStatus) => {
    try {
      await api.put(`/waiter-calls/${callId}/status`, { status: newStatus });
      toast.success(`Waiter call ${newStatus === "handled" ? "marked as handled" : "cancelled"}`);
      loadAll();
    } catch {
      toast.error("Unable to update the waiter call");
    }
  };

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Loading dashboard" : "Dashboard unavailable"} description={loadError ? "Unable to load data. Check your connection and try again." : undefined} retryLabel="Try again" onRetry={loadError ? loadAll : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page operations-dashboard" data-testid="admin-dashboard" onClick={unlockAudio}>
      {loadError && <PageState kind="error" title="Unable to refresh dashboard" description={loadError ? "Unable to load data. Check your connection and try again." : undefined} retryLabel="Try again" onRetry={loadAll} />}
      <DashboardControls soundOn={soundOn} onToggleSound={toggleSound} onTestBell={() => { unlockAudio(); setTimeout(() => playWaiterCallAlert(), 50); }} waStatus={waStatus} waTesting={waTesting} onTestWhatsApp={testWhatsApp} connected={connected} refreshing={refreshing} onRefresh={loadAll} />

      <div className="operations-kpis">
        <DashboardKpi label="Total Orders" value={stats.total_orders} hint="Daily summary" icon={ShoppingBag} testid="kpi-total-orders" />
        <DashboardKpi label="Pending Orders" value={stats.pending} hint="Awaiting confirmation" icon={Bell} accent testid="kpi-pending" />
        <DashboardKpi label="Preparing" value={stats.preparing} hint="In the kitchen" icon={ChefHat} testid="kpi-preparing" />
        <DashboardKpi label="Revenue" value={formatIDR(stats.total_revenue)} hint={`${stats.paid ?? 0} paid order${stats.paid === 1 ? "" : "s"}`} currency icon={DollarSign} testid="kpi-revenue" />
        <DashboardKpi label="Unpaid Orders" value={stats.unpaid ?? 0} hint={formatIDR(stats.unpaid_revenue ?? 0)} icon={Wallet} testid="kpi-unpaid" />
      </div>

      {waiterCalls.length > 0 && <section tabIndex={0} className="operations-waiter-calls rounded-xl bg-[#ffddb1]/40 p-4" aria-label="Waiter calls"><h2 className="mb-3 flex items-center gap-2 font-sans text-base font-semibold text-[#614000]"><Bell className="h-5 w-5" />Waiter Calls ({waiterCalls.length})</h2><div className="space-y-2">{waiterCalls.map((call) => <div key={call.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3"><div className="min-w-0 flex-1"><p className="text-sm font-semibold">Table {call.table_number}</p><p className="mt-0.5 break-words text-xs text-muted-foreground">{call.reason === "Panggil Pelayan" ? "Waiter requested" : call.reason} · {timeAgo(call.created_at)}</p></div><div className="flex shrink-0 gap-2"><Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg bg-[#007b5b] text-white hover:bg-[#005d45] hover:text-white" onClick={() => handleWaiterCall(call.id, "handled")} aria-label={`Handle waiter call for table ${call.table_number}`} data-testid={`handle-waiter-${call.id}-btn`}><Check className="h-4 w-4" /></Button><Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => handleWaiterCall(call.id, "cancelled")} aria-label={`Cancel waiter call for table ${call.table_number}`} data-testid={`cancel-waiter-${call.id}-btn`}><X className="h-4 w-4" /></Button></div></div>)}</div></section>}

      <OrderBoard orders={orders} onAdvance={updateStatus} onPrint={printKitchen} onInspect={setDetailId} busyIds={updatingIds} />
      <section className="operations-starter" data-testid="admin-starter-links"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#ffdbd0] text-[#943512]"><SlidersHorizontal className="h-5 w-5" /></span><div className="min-w-0 flex-1"><h2 className="font-sans text-base font-semibold">Order Management</h2><p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">Manage room and table orders from your operations console.</p></div><Link to="/admin/orders" className="operations-open-orders">View Orders<ArrowRight className="h-4 w-4" /></Link></section>
      <OrderDetails order={detailOrder} onClose={() => setDetailId(null)} onPrint={printKitchen} />
    </div>
  );
}
